import { ZButton } from "@/shared/components/button";
import FileUpload from "@/shared/components/custom/file-upload";
import { UploadedFile } from "@/shared/types/file-upload";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import { File } from "lucide-react";
import { useState } from "react";
import { downloadControlsTemplate, importControls } from "../../api/controls";
import { addNotification } from "@/shared/states/notification";
import { useCustomizeControlsStore } from "../../states/cusomize-controls";
import { AgentSessionType } from "@/modules/sessions/types";
import SpinnerImage from "@/shared/images/spinner.gif";

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (files: UploadedFile[]) => void;
  loading?: boolean;
  sessionId: string; // Add sessionId prop
}

const ImportModal = ({
  isOpen,
  onClose,
  onImport,
  loading,
  sessionId, // Accept sessionId
}: ImportModalProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const { agentType } = useCustomizeControlsStore();

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);
      
      // Map agent type to API parameter
      let templateType = "soc2_type2"; // default
      if (agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) {
        templateType = "soc2_type2"; // Both type 1 and type 2 use soc2_type2
      } else if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        templateType = "multi_file_vendor_assessment";
      }
      
      await downloadControlsTemplate(templateType);
      addNotification({
        message: "Template downloaded successfully",
        type: "success",
      });
    } catch (error) {
      addNotification({
        message: "Failed to download template",
        type: "error",
      });
      console.error("Download error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImport = async () => {
    try {
      setIsImporting(true);
      
      // Map agent type to API parameter
      let templateType = "soc2_type2"; // default
      if (agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) {
        templateType = "soc2_type2"; // Both type 1 and type 2 use soc2_type2
      } else if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        templateType = "multi_file_vendor_assessment";
      }
      
      const result = await importControls(uploadedFiles, templateType, sessionId);
      
      if (result.success) {
        addNotification({
          title: "Import Successful",
          message: `${result.importedCount} controls imported to the assessment.`,
          type: "success",
        });
        
        // Call onImport to trigger data refresh
        onImport(uploadedFiles);
        setUploadedFiles([]);
        
        // Add 500ms delay before closing the modal for better UX
        setTimeout(() => {
          onClose();
        }, 500);
      }
    } catch (error) {
      addNotification({
        title: "Import Failed",
        message: "Zania was unable to import controls from the file. Ensure the format of the file is correct and try again.",
        type: "error",
      });
      console.error("Import error:", error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
  };

  const hasValidFiles = uploadedFiles.some(file => file.status === "success");
  const isProcessing = isDownloading || isImporting || loading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" className="max-w-[500px]">
      <ModalContent>
        <ModalHeader className="flex flex-col items-center gap-2 p-6">
          <h2 className="font-semibold text-[18px] leading-[28px] tracking-[0%] text-center text-[#11181C]">
            Import Controls
          </h2>
          {!isImporting && (
            <p className="text-[14px] font-normal text-[#F31260] text-center">
              Note that all existing controls will be overridden.
            </p>
          )}
        </ModalHeader>

        {isImporting ? (
          <div className="min-h-[500px]">
          <div className="flex flex-col items-center justify-center py-40 ">
            <img 
              src={SpinnerImage}
              alt="Loading..." 
              className="w-16 h-16"
            />
          </div>
          <p className="text-lg text-center mt-4 text-[#171717]">AI is importing controls from your file</p>
          </div>
        ) : (
          <>
            <ModalBody className="px-6 pb-0">
              <div className="flex flex-col gap-4">
                {/* Step 1: Download Template */}
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-[14px] font-medium text-[#11181C]">
                    Step 1: Download Template
                  </h3>
                  <p className="text-[14px] text-[#71717A]">
                    Use this Excel file to ensure your control data is formatted correctly.
                  </p>
                  <ZButton
                    variant="bordered"
                    className="w-fit mt-1"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    isDisabled={isDownloading || isImporting}
                  >
                    {"Download Template"
                    }
                  </ZButton>
                </div>

                {/* Step 2: Fill in Details */}
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-[14px] font-medium text-[#11181C]">
                    Step 2: Fill in Details
                  </h3>
                  <p className="text-[14px] text-[#71717A]">
                    Add your control data into the downloaded file.
                  </p>
                </div>

                {/* Step 3: Upload Completed File */}
                <div className="flex flex-col gap-1.5 mb-4">
                  <h3 className="text-[14px] font-medium text-[#11181C]">
                    Step 3: Upload Completed File
                  </h3>
                  <FileUpload
                    isMultiple={false}
                    maxFiles={1}
                    allowedFileTypes={[
                      "text/csv",
                      "application/vnd.ms-excel",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    ]}
                    setFiles={handleFileChange}
                    maxSizeMB={20}
                  />
                </div>
              </div>
            </ModalBody>

            <ModalFooter className="flex gap-2 pt-4 px-6 pb-6 border-t justify-end">
              <ZButton
                variant="bordered"
                size="sm"
                onClick={onClose}
                isDisabled={isDownloading || isImporting}
              >
                Cancel
              </ZButton>
              <ZButton
                color="primary"
                size="sm"
                onClick={handleImport}
                isLoading={isImporting}
                isDisabled={!hasValidFiles || isDownloading || isImporting}
              >Import
              </ZButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ImportModal;
